import { useContext, useEffect, useMemo, useState } from "react";
import { ClassContext } from "../app";
import { useParams } from "react-router-dom";
import { fetchData } from "../helpers/fetch-data";
import Header from "../components/header";
import Footer from "../components/footer";
import PROFILES from "../data/profiles";
import translate from "../locales";
import Select from "react-select";
import Markdown from "react-markdown";
import clsx from "clsx";

export function Stats() {
  const { profiles, setProfiles } = useContext(ClassContext);
  const [selectedProfileId, setSelectedProfileId] = useState(null);
  const params = useParams();

  async function loadData() {
    if (profiles.length === 0) {
      const data = await fetchData(params.id);
      setProfiles(data);
    }
  }

  useEffect(() => {
    loadData();
  }, []);

  const profileOptions = useMemo(
    () =>
      Object.entries(PROFILES).map(([id, data]) => ({
        value: id,
        label: translate(`dashboard.profiles.${data.id}`),
      })),
    [],
  );

  useEffect(() => {
    if (!profiles || Array.isArray(profiles)) {
      return;
    }

    setSelectedProfileId((prev) => (prev === null ? prev : null));
  }, [profiles]);

  const selectedProfileOption = useMemo(
    () =>
      profileOptions.find((option) => option.value === selectedProfileId) ??
      null,
    [profileOptions, selectedProfileId],
  );

  const selectedProfileMeta =
    selectedProfileId !== null ? PROFILES[selectedProfileId] : null;

  const actionSections = useMemo(() => {
    if (
      selectedProfileId === null ||
      !selectedProfileMeta ||
      !profiles ||
      Array.isArray(profiles)
    ) {
      return [];
    }

    const categories = [
      {
        key: "actionHabits",
        title: translate("dashboard.dropdown.habits"),
        choices: selectedProfileMeta.habits?.choices,
      },
      {
        key: "actionEnvironment",
        title: translate("dashboard.dropdown.environment"),
        choices: selectedProfileMeta.environment?.choices,
      },
      {
        key: "actionDevices",
        title: translate("dashboard.dropdown.devices"),
        choices: selectedProfileMeta.devices?.choices,
      },
    ];

    return categories.map(({ key, title, choices }) => {
      const profileData = profiles?.[key]?.[selectedProfileId];
      const actions = profileData?.actions ?? {};

      const entries = Object.entries(choices ?? {}).map(([actionId, label]) => {
        const numericActionId = Number(actionId);
        const stats = actions?.[numericActionId] ?? {
          percentage: 0,
          count: 0,
        };
        return {
          id: numericActionId,
          label,
          percentage: stats.percentage ?? 0,
          count: stats.count ?? 0,
        };
      });

      const highestPercentage =
        entries.length === 0
          ? null
          : Math.max(...entries.map((entry) => entry.percentage ?? 0));

      const enrichedEntries = entries.map((entry) => ({
        ...entry,
        isTop:
          highestPercentage !== null &&
          highestPercentage !== 0 &&
          entry.percentage === highestPercentage,
      }));

      return {
        key,
        title,
        entries: enrichedEntries,
      };
    });
  }, [profiles, selectedProfileId, selectedProfileMeta]);

  return (
    <>
      <title>{translate("dashboard.stats.title")}</title>
      <Header />
      <div className="page page--dashboard">
        <div className="stats-container">
          <div className="stats stats--beige">
            <p className="stats__groupcode">
              {translate("dashboard.group_code")} {profiles?.groupCode}
            </p>
            <h1 className="stats__header">{translate("dashboard.header")}</h1>
            <div className="stats__cards">
              {Object.values(PROFILES).map((data, index) => (
                <div className="card" key={data.id}>
                  <img
                    className="card__image"
                    src={data.image}
                    alt={translate(`profiles.images.${data.id}`)}
                  />
                  <div className="card__content">
                    <span className="card__percentage">
                      {`${profiles?.profiles?.[index + 1]?.percentage ?? 0}%`}
                    </span>
                    <span className="card__text">
                      {translate(`dashboard.profiles.${data.id}`)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="stats">
          <h2 className="stats__header">
            {translate("dashboard.dropdown.header")}{" "}
          </h2>
          <div className="dropdown">
            <span className="dropdown__header">
              {translate("dashboard.dropdown.select.header")}{" "}
            </span>
            <Select
              classNamePrefix="dropdown"
              options={profileOptions}
              value={selectedProfileOption}
              isSearchable={false}
              blurInputOnSelect={true}
              openMenuOnFocus={false}
              unstyled={true}
              onChange={(option) =>
                setSelectedProfileId(option ? option.value : null)
              }
              placeholder={translate("dashboard.dropdown.select.placeholder")}
              isClearable={false}
              components={{
                IndicatorSeparator: () => null,
              }}
            />
          </div>
          {selectedProfileId !== null && actionSections.length > 0 ? (
            <div className="actions">
              {actionSections.map((section) => (
                <div className="actions__section" key={section.key}>
                  <h3 className="actions__title">
                    <Markdown>{section.title}</Markdown>
                  </h3>
                  <ul className="actions__list">
                    {section.entries.map((entry) => (
                      <li
                        className={clsx("actions__item", {
                          "actions__item--top": entry.isTop,
                        })}
                        key={entry.id}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="actions__icon"
                          viewBox="0 0 27 25"
                          fill="none"
                        >
                          <path
                            d="M4.98188 24.7466L7.09836 15.5969L0 9.44278L9.37766 8.62875L13.0245 0L16.6714 8.62875L26.049 9.44278L18.9507 15.5969L21.0672 24.7466L13.0245 19.895L4.98188 24.7466Z"
                            fill="currentcolor"
                          />
                        </svg>
                        <span className="actions__percentage">
                          {entry.percentage}%
                        </span>
                        <span className="actions__label">
                          <Markdown>{entry.label}</Markdown>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>
      <Footer nonStick={true} />
    </>
  );
}
